import { useEffect, useRef, useState } from 'react';
import type { Channel, PublicUser } from '@orbit-sync/shared-types';
import { useChatStore } from '../chat/chatStore.js';
import { api, errorMessage } from '../lib/api/index.js';
import { Icon } from './Icon.js';
import { PeoplePicker } from './PeoplePicker.js';

/**
 * "Add people" for a group. Any member may add anyone - including someone who
 * left - and the new members see the conversation's history.
 */
export function AddMembersDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const upsertChannel = useChatStore((state) => state.upsertChannel);

  const [picked, setPicked] = useState<PublicUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ref.current?.showModal();
    searchRef.current?.focus();
  }, []);

  function toggle(user: PublicUser): void {
    setPicked((current) =>
      current.some((item) => item.id === user.id)
        ? current.filter((item) => item.id !== user.id)
        : [...current, user],
    );
  }

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      upsertChannel(
        await api.channels.addMembers(
          channel.id,
          picked.map((user) => user.id),
        ),
      );
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="modal" onClose={onClose} aria-labelledby="add-members-title">
      <header className="modal-head">
        <div>
          <h2 id="add-members-title">Add people</h2>
          <p className="truncate">to {channel.name}</p>
        </div>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="modal-scroll">
        {picked.length > 0 ? (
          <div className="chips">
            {picked.map((user) => (
              <button key={user.id} type="button" className="chip" onClick={() => toggle(user)}>
                {user.displayName}
                <Icon name="x" size={12} />
              </button>
            ))}
          </div>
        ) : null}

        <PeoplePicker
          ref={searchRef}
          pickedIds={picked.map((user) => user.id)}
          excludeIds={channel.memberIds}
          disabled={busy}
          onChoose={toggle}
          actionIcon={(chosen) => (chosen ? 'check' : 'plus')}
          actionLabel={(_user, chosen) => (chosen ? 'Added' : 'Add')}
        />

        {error ? (
          <p className="field-error" role="alert">
            <Icon name="alertCircle" size={13} />
            {error}
          </p>
        ) : null}
      </div>

      <footer className="modal-foot">
        <span className="field-hint">
          {picked.length === 0 ? 'Choose who to add' : `${picked.length} selected`}
        </span>
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || picked.length === 0}
          onClick={() => void submit()}
        >
          {busy ? <span className="btn-spinner" /> : <Icon name="userPlus" size={14} />}
          Add
        </button>
      </footer>
    </dialog>
  );
}
